/**
 * Error mapping utility to convert backend error messages to user-friendly frontend messages.
 * This ensures users never see technical errors or implementation details.
 */

interface ErrorResponse {
  response?: {
    status?: number;
    data?: {
      detail?: string;
      error?: string;
      message?: string;
    };
  };
  message?: string;
  code?: string;
}

/**
 * Maps technical error messages to user-friendly messages.
 * @param error - The error object from API call or exception
 * @returns User-friendly error message
 */
export function mapErrorToUserMessage(error: any): string {
  if (!error) return 'An unexpected error occurred. Please try again.';

  // Extract the error message from various possible structures
  const errorResponse = error as ErrorResponse;
  const statusCode = errorResponse.response?.status;
  const detail = errorResponse.response?.data?.detail || '';
  const errorMsg = errorResponse.response?.data?.error || '';
  const message = typeof error === 'string' ? error : errorMsg || detail || errorResponse.message || '';
  const lower = message.toLowerCase();

  // HTTP Status Codes - Handle first for precise mapping
  if (statusCode === 401) {
    if (lower.includes('expired')) return 'Your session has expired. Please sign in again.';
    if (lower.includes('invalid')) return 'Your session is invalid. Please sign in again.';
    return 'Authentication required. Please sign in to continue.';
  }
  if (statusCode === 403) {
    return 'You don\'t have permission to perform this action. Please contact your administrator.';
  }
  if (statusCode === 404) {
    if (lower.includes('company')) return 'Company not found. Please check your account settings.';
    if (lower.includes('integration')) return 'Integration not found. Please check your connections.';
    if (lower.includes('transaction')) return 'Transaction not found.';
    return 'The requested resource was not found.';
  }
  if (statusCode === 409) {
    if (lower.includes('registration')) return 'This tax registration already exists. Your existing registration has been retrieved.';
    if (lower.includes('email')) return 'An account with this email already exists. Please sign in or use a different email.';
    return 'This resource already exists.';
  }
  if (statusCode === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (statusCode === 500 || statusCode === 502 || statusCode === 503) {
    return 'Server error occurred. Our team has been notified. Please try again in a few moments.';
  }
  if (statusCode === 504) {
    return 'Request timed out. Please check your internet connection and try again.';
  }

  // Authentication & Authorization Errors
  if (lower.includes('invalid credentials') || lower.includes('incorrect password') || lower.includes('wrong password')) {
    return 'Invalid email or password. Please try again.';
  }
  if (lower.includes('email already') || lower.includes('already registered')) {
    return 'An account with this email already exists. Please sign in or use a different email.';
  }
  if (lower.includes('inactive user') || lower.includes('account inactive')) {
    return 'Your account is inactive. Please contact support for assistance.';
  }
  if (lower.includes('permission') || lower.includes('not authorized') || lower.includes('access denied')) {
    return 'You don\'t have permission to perform this action.';
  }
  if (lower.includes('token expired') || lower.includes('expired token')) {
    return 'Your session has expired. Please sign in again.';
  }
  if (lower.includes('could not validate credentials') || lower.includes('not authenticated')) {
    return 'Authentication required. Please sign in to continue.';
  }

  // Tax Registration Errors
  if (lower.includes('already added a registration') || (lower.includes('registration') && lower.includes('already exists'))) {
    return 'This tax registration already exists. Your existing registration has been retrieved.';
  }
  if (lower.includes('tax registration') && lower.includes('missing')) {
    return 'Required tax registration information is missing. Please provide all required fields.';
  }
  if (lower.includes('invalid state')) {
    return 'Please provide a valid state code (e.g., \'CA\', \'NY\').';
  }
  if (lower.includes('invalid country')) {
    return 'Please provide a valid country code (e.g., \'US\', \'CA\').';
  }

  // Integration Errors (QuickBooks, Shopify, etc.)
  if (lower.includes('invalid_grant') || lower.includes('authorization code')) {
    return 'Your QuickBooks connection has expired. Please reconnect your QuickBooks account.';
  }
  if (lower.includes('realm_id')) {
    return 'QuickBooks company ID is missing. Please try reconnecting to QuickBooks.';
  }
  if (lower.includes('quickbooks')) {
    return 'Unable to connect to QuickBooks. Please check your credentials and try again.';
  }
  if (lower.includes('shopify')) {
    return 'Unable to connect to Shopify. Please check your credentials and try again.';
  }

  // Company & Business Errors
  if (lower.includes('company not found')) {
    return 'Company not found. Please check your account settings.';
  }
  if (lower.includes('not associated with any company')) {
    return 'You are not associated with any company. Please complete your profile setup.';
  }
  if (lower.includes('tax id already exists')) {
    return 'A company with this Tax ID already exists in our system.';
  }

  // Network & Connection Errors
  if (lower.includes('network error') || lower.includes('network_error')) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  if (lower.includes('timeout') || lower.includes('timed out')) {
    return 'Request timed out. Please check your internet connection and try again.';
  }
  if (lower.includes('cors') || lower.includes('cross-origin')) {
    return 'Connection blocked by browser security. Please refresh the page and try again.';
  }
  if (lower.includes('failed to fetch') || lower.includes('fetch failed')) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  // Validation Errors
  if (lower.includes('valid email') || (lower.includes('email') && lower.includes('invalid'))) {
    return 'Please enter a valid email address.';
  }
  if (lower.includes('password') && (lower.includes('short') || lower.includes('least'))) {
    return 'Password must be at least 8 characters long.';
  }
  if (lower.includes('required') || lower.includes('cannot be empty')) {
    return 'Please fill in all required fields.';
  }
  if (lower.includes('invalid format') || lower.includes('format invalid')) {
    return 'Invalid format. Please check your input and try again.';
  }

  // Transaction Errors
  if (lower.includes('transaction') && lower.includes('calculate')) {
    return 'Unable to calculate tax for this transaction. Please verify the transaction details.';
  }
  if (lower.includes('transaction') && lower.includes('invalid')) {
    return 'Invalid transaction data. Please check all required fields.';
  }

  // Generic HTTP Errors
  if (lower.includes('server error') || lower.includes('internal server error')) {
    return 'A server error occurred. Our team has been notified. Please try again in a few moments.';
  }
  if (lower.includes('service unavailable') || lower.includes('temporarily unavailable')) {
    return 'Service is temporarily unavailable. Please try again in a few minutes.';
  }
  if (lower.includes('bad request')) {
    return 'Invalid request. Please check your input and try again.';
  }
  if (lower.includes('not found') && !lower.includes('company') && !lower.includes('integration')) {
    return 'The requested resource was not found.';
  }

  // Tax engine errors
  if (lower.includes('tax engine') && !lower.includes('already')) {
    return 'Payment processing error. Please try again or contact support.';
  }

  // Check if message is already user-friendly (short, no technical terms)
  const technicalTerms = [
    'exception', 'traceback', 'stack', 'line ', 'error at',
    'typeerror', 'valueerror', 'attributeerror', 'null pointer',
    'undefined', 'syntaxerror', 'referenceerror', 'api error',
    'http/1.1', 'status_code', 'detail=', '[object object]'
  ];
  
  const containsTechnicalTerms = technicalTerms.some(term => lower.includes(term));
  
  if (message && message.length < 150 && !containsTechnicalTerms) {
    // Message appears to be user-friendly already
    return message;
  }

  // Generic fallback - don't expose technical details
  return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
} 